import {
  DebouncedSearchInput,
  ListHeader,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  ModalOpenButton,
  useFormatting,
  useScreenWidth,
} from "@msys/ui";
import { Box, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  OrganisationProjectsSorting,
  ProjectStateMachineStatus,
} from "../../../clients/graphqlTypes.js";
import { useUserData } from "../../auth/useUserData.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { ProjectListItemDisplayConfig } from "../../features/projects/components/ProjectListItem.js";
import { defaultListItemDisplayConfig } from "../../features/projects/components/ProjectsListItemVisibilitySettingsModal.js";
import { FilterRelevantToMe } from "../../features/projects/filters/index.js";
import { ProjectsFilterModal } from "../../features/projects/ProjectsFilterModal.js";
import {
  type Filters,
  ProjectsList,
} from "../../features/projects/ProjectsList.js";
import { getProjectFilterChipsFactory } from "../../features/projects/renderProjectsFilterChips.js";
import { PROJECT_FILTER_MAP } from "../../features/projects/useProjectSources.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { useUserPreference } from "../../features/users/useUserPreference.js";

const DEFAULT_ITEMS_PER_PAGE = 25;
const DEFAULT_SORTING: OrganisationProjectsSorting[] = [
  {
    column: "createdAt",
    direction: "desc",
  },
];

const ALLOWED_VIEWS: CollectionView[] = ["table", "list"];

interface Props {
  submenuItems: PageTopbarItem[];
}

export const ProjectsPast = ({ submenuItems }: Props) => {
  const { t } = useTranslate([
    "Projects",
    "Global",
    "Tickets",
    "Opportunities",
  ]);
  const viewer = useUserData().currentUser!;
  const { getFormattedPrice, getFormattedFloat } = useFormatting();
  const { isMinTablet } = useScreenWidth();

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "projects-past",
      "table",
      "list"
    );

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<OrganisationProjectsSorting, Filters>(
    {},
    DEFAULT_SORTING,
    DEFAULT_ITEMS_PER_PAGE
  );

  const baseVariables = React.useMemo(
    () => ({
      offset,
      limit,
      sorting: DEFAULT_SORTING,
    }),
    [limit, offset]
  );

  const allowedStates: ProjectStateMachineStatus[] = React.useMemo(
    () =>
      viewer.organisation.isClientOrganisation
        ? ["completed", "cancelled", "contractee_declined", "legacy_closed"]
        : ["completed", "cancelled", "legacy_closed"],
    [viewer.organisation.isClientOrganisation]
  );

  const variables = React.useMemo(
    () => ({
      ...baseVariables,
      ...filters,
      filterBySources: filters.sources
        ? filters.sources.map(s => PROJECT_FILTER_MAP[s]).flat(1)
        : undefined,
      filterIncludeState: filters.filterIncludeState?.length
        ? filters.filterIncludeState
        : allowedStates,
      sorting,
    }),
    [allowedStates, baseVariables, filters, sorting]
  );

  const listViewName = "ProjectsPast";
  const stateStore = useDataGridStateStore(listViewName);
  const listItemDisplayConfigStore =
    useUserPreference<ProjectListItemDisplayConfig>(
      `DisplayConfig-${listViewName}`,
      defaultListItemDisplayConfig
    );

  const getActiveProjectFilterChips = getProjectFilterChipsFactory({
    viewer,
    t,
    allowedStates,
    getFormattedFloat,
    showPhasesChip: activeView !== "kanban",
  });
  const activeFilterChips = getActiveProjectFilterChips({
    filters,
    setFilters,
  });

  return (
    <Page
      title={t("Past", { ns: "Projects" })}
      subtitle={t("Projects", { ns: "Projects" })}
      submenuItems={submenuItems}
    >
      <PageContainer>
        <ListHeader
          SwitchViewButton={
            <SwitchCollectionViewButton
              allowedViews={ALLOWED_VIEWS}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          }
          FilterButton={
            <ModalOpenButton
              Modal={ProjectsFilterModal}
              modalProps={{
                sorting,
                setSorting,
                filters,
                setFilters,
                resetFilters,
                allowedStates,
                showPhasesFilter: activeView !== "kanban",
                showSorting: activeView !== "kanban",
              }}
            >
              <FilterButton />
            </ModalOpenButton>
          }
          FilterFields={
            isMinTablet ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: 0,
                  pl: 1.5,
                }}
              >
                <FilterRelevantToMe filters={filters} setFilters={setFilters} />
              </Box>
            ) : undefined
          }
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", { ns: "Global" })}
              defaultValue={filters.filterSearchTerm}
              onChangeSearchTerm={(newValue: string) => {
                setFilters(filters => ({
                  ...filters,
                  filterSearchTerm: newValue,
                }));
              }}
            />
          }
          ActiveFilterChips={
            <ListHeaderActiveFilters
              activeFilterChips={activeFilterChips}
              collapsedContent={
                <Typography variant="body2">
                  {t("Active filters", { ns: "Global" })}
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      marginLeft: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {t("{count} results", {
                      ns: "Global",
                      count: activeFilterChips.length,
                    })}
                  </Typography>
                </Typography>
              }
              clearAllButton={
                <ListHeaderActiveFiltersActionButton
                  label={t("Clear all", { ns: "Global" })}
                  onClick={resetFilters}
                />
              }
            />
          }
          sx={{ mb: 2 }}
        />
        <ProjectsList
          sorting={sorting}
          setSorting={setSorting}
          activeView={activeView}
          pathToPage={"/projects/past"}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          stateStore={stateStore}
          variables={variables}
          displayConfig={listItemDisplayConfigStore.value}
        />
      </PageContainer>
    </Page>
  );
};
